import { useCallback } from 'react';
import type { ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faPencil } from '@fortawesome/free-solid-svg-icons';
import { AdminSectionHeading } from '../../admin-ui';
import type { ThemeSettingsPageStyles } from './themeSettingsPageStyles';
import type { ThemeColorsSectionViewModel } from './useThemeColorSettings';
import type { ColorRowSpec } from './themeSettingsUtils';

function formatHex(value: string): string {
  if (!value) return '';
  return value.startsWith('#') ? value.toUpperCase() : `#${value.toUpperCase()}`;
}

interface ColorRowProps {
  spec: ColorRowSpec;
  value: string;
  dirty: boolean;
  onChange: (id: string, value: string) => void;
  styles: ThemeSettingsPageStyles;
}

function ColorRow({ spec, value, dirty, onChange, styles }: ColorRowProps) {
  const handleColorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(spec.id, event.target.value);
    },
    [onChange, spec.id]
  );

  return (
    <div style={styles.colorRow}>
      <span style={styles.colorRowLabel}>
        {spec.label}
        {spec.detail ? <span style={styles.colorRowLabelDetail}>{spec.detail}</span> : null}
      </span>
      <label style={{ ...styles.colorInput, ...(dirty ? styles.dirtyControl : {}) }} aria-label={`Edit ${spec.label}`}>
        <div style={styles.colorInputLeft}>
          <span style={styles.colorSwatch(value)} aria-hidden="true" />
          <span style={styles.colorHexText}>{formatHex(value)}</span>
        </div>
        <span style={styles.colorEditIcon} aria-hidden="true">
          <FontAwesomeIcon icon={faPencil} style={{ fontSize: 18 }} />
          <input
            type="color"
            value={value}
            onChange={handleColorChange}
            style={styles.hiddenColorInput}
            aria-label={`Color picker for ${spec.label}`}
          />
        </span>
      </label>
    </div>
  );
}

export type ThemeColorsSectionProps = ThemeColorsSectionViewModel & { styles: ThemeSettingsPageStyles; dirtyColors?: Record<string, boolean> };

export function ThemeColorsSection({
  styles,
  dirtyColors = {},
  themeColorSpecs,
  lessonColorSpecs,
  colors,
  onColorChange,
}: ThemeColorsSectionProps) {
  return (
    <section style={styles.card} aria-labelledby="theme-settings-colors">
      <AdminSectionHeading icon={faPalette} title="Colors" headingLevel="h2" titleId="theme-settings-colors" />
      <div style={styles.colorsBody}>
        <div style={styles.colorsColumn}>
          <h3 style={styles.colorsColumnTitle}>Theme colors</h3>
          {themeColorSpecs.map((spec) => (
            <ColorRow
              key={spec.id}
              spec={spec}
              dirty={Boolean(dirtyColors[spec.id])}
              value={colors[spec.id] ?? spec.defaultValue}
              onChange={onColorChange}
              styles={styles}
            />
          ))}
        </div>

        <div style={styles.colorsColumn}>
          <h3 style={styles.colorsColumnTitle}>Lesson colors</h3>
          {lessonColorSpecs.map((spec) => (
            <ColorRow
              key={spec.id}
              spec={spec}
              dirty={Boolean(dirtyColors[spec.id])}
              value={colors[spec.id] ?? spec.defaultValue}
              onChange={onColorChange}
              styles={styles}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
